import { CameraMeta } from '@/types';

export const normalizeText = (text: string): string => {
    if (!text) return '';
    return text
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
        .replace(/\s+/g, ' ')           // Collapse multiple spaces
        .trim()
        .toUpperCase();
};

export const stripTrailingPunctuation = (text: string): string => {
    return text.replace(/[:\.;—\-]+$/, '').trim();
};

export interface DictionaryEntry {
    en: string[];
    pt: string[];
    es: string[];
    fr: string[];
}

export const SHOT_CUE_DICTIONARY: Record<string, Record<string, DictionaryEntry>> = {
    scale: {
        ELS: {
            en: ["EXTREME LONG SHOT", "EXTREME WIDE", "ELS"],
            pt: ["PLANO EXTREMO GERAL", "EXTREMO GERAL", "ELS"],
            es: ["PLANO GENERAL EXTREMO", "GENERAL EXTREMO", "ELS"],
            fr: ["PLAN TRES LARGE", "PLAN GENERAL EXTREME", "ELS"]
        },
        LS: {
            en: ["LONG SHOT", "WIDE SHOT", "WS", "LS"],
            pt: ["PLANO GERAL", "GERAL", "LS"],
            es: ["PLANO GENERAL", "GENERAL", "LS"],
            fr: ["PLAN LARGE", "PLAN GENERAL", "LS"]
        },
        FS: {
            en: ["FULL SHOT", "FS"],
            pt: ["PLANO INTEIRO", "INTEIRO", "FS"],
            es: ["PLANO ENTERO", "ENTERO", "FS"],
            fr: ["PLAN EN PIED", "PLEIN PIED", "FS"]
        },
        MLS: {
            en: ["MEDIUM LONG SHOT", "MLS", "AMERICAN SHOT"],
            pt: ["PLANO AMERICANO", "AMERICANO", "MLS"],
            es: ["PLANO AMERICANO", "AMERICANO", "MLS"],
            fr: ["PLAN AMERICAIN", "AMERICAIN", "MLS"]
        },
        MS: {
            en: ["MEDIUM SHOT", "MS"],
            pt: ["PLANO MEDIO", "MEDIO", "MS"],
            es: ["PLANO MEDIO", "MEDIO", "MS"],
            fr: ["PLAN MOYEN", "MOYEN", "MS"]
        },
        MCU: {
            en: ["MEDIUM CLOSE UP", "MCU"],
            pt: ["PLANO MEDIO CURTO", "MEIO PRIMEIRO PLANO", "MCU"],
            es: ["PLANO MEDIO CORTO", "MEDIO CORTO", "MCU"],
            fr: ["PLAN RAPPROCHE", "RAPPROCHE", "MCU"]
        },
        CU: {
            en: ["CLOSE UP", "CLOSE-UP", "CU"],
            pt: ["CLOSE", "CLOSE UP", "PRIMEIRO PLANO", "CU"],
            es: ["PRIMER PLANO", "CLOSE UP", "CU"],
            fr: ["GROS PLAN", "CLOSE UP", "CU"]
        },
        ECU: {
            en: ["EXTREME CLOSE UP", "ECU"],
            pt: ["PRIMEIRISSIMO PLANO", "CLOSE EXTREMO", "ECU"],
            es: ["PRIMERISIMO PLANO", "CLOSE EXTREMO", "ECU"],
            fr: ["TRES GROS PLAN", "GROS PLAN EXTREME", "ECU"]
        },
        INSERT: {
            en: ["INSERT", "CUT IN", "CUT-IN", "INSERT SHOT"],
            pt: ["PLANO DETALHE", "DETALHE", "INSERT"],
            es: ["PLANO DETALLE", "DETALLE", "INSERT"],
            fr: ["PLAN DETAIL", "DETAIL", "INSERT"]
        },
        MACRO: {
            en: ["MACRO", "MACRO SHOT"],
            pt: ["MACRO", "PLANO MACRO"],
            es: ["MACRO", "PLANO MACRO"],
            fr: ["MACRO", "PLAN MACRO"]
        }
    },
    angle: {
        EYE: {
            en: ["EYE LEVEL", "NEUTRAL"],
            pt: ["NIVEL DOS OLHOS", "NORMAL"],
            es: ["NIVEL DE LOS OJOS", "NORMAL"],
            fr: ["A HAUTEUR DES YEUX", "NEUTRE"]
        },
        HIGH: {
            en: ["HIGH ANGLE"],
            pt: ["PLONGEE", "DE CIMA"],
            es: ["PICADO", "DESDE ARRIBA"],
            fr: ["PLONGEE"]
        },
        LOW: {
            en: ["LOW ANGLE"],
            pt: ["CONTRA PLONGEE", "DE BAIXO"],
            es: ["CONTRAPICADO", "DESDE ABAJO"],
            fr: ["CONTRE PLONGEE"]
        },
        TOP: {
            en: ["TOP SHOT", "OVERHEAD"],
            pt: ["ZENITAL", "VISTA DE CIMA"],
            es: ["CENITAL", "VISTA DESDE ARRIBA"],
            fr: ["ZENITHAL", "VUE PLONGEANTE"]
        },
        NADIR: {
            en: ["NADIR"],
            pt: ["NADIR"],
            es: ["NADIR"],
            fr: ["NADIR"]
        },
        DUTCH: {
            en: ["DUTCH ANGLE", "DUTCH TILT"],
            pt: ["PLANO HOLANDES", "CAMERA INCLINADA"],
            es: ["ANGULO HOLANDES", "CAMARA INCLINADA"],
            fr: ["ANGLE HOLLANDAIS", "CAMERA INCLINEE"]
        }
    },
    type: {
        POV: {
            en: ["POV", "POINT OF VIEW"],
            pt: ["POV", "PONTO DE VISTA"],
            es: ["POV", "PUNTO DE VISTA"],
            fr: ["POV", "POINT DE VUE"]
        },
        OTS: {
            en: ["OVER THE SHOULDER", "OTS"],
            pt: ["SOBRE O OMBRO", "OTS"],
            es: ["SOBRE EL HOMBRO", "OTS"],
            fr: ["PAR DESSUS L EPAULE", "OTS"]
        },
        TWO_SHOT: {
            en: ["TWO SHOT", "2 SHOT", "2-SHOT"],
            pt: ["TWO SHOT", "DOIS PERSONAGENS"],
            es: ["DOS PERSONAJES", "TWO SHOT"],
            fr: ["DEUX PERSONNAGES", "TWO SHOT"]
        },
        GROUP: {
            en: ["GROUP SHOT"],
            pt: ["PLANO DE GRUPO"],
            es: ["PLANO DE GRUPO"],
            fr: ["PLAN DE GROUPE"]
        },
        REACTION: {
            en: ["REACTION SHOT"],
            pt: ["PLANO DE REACAO", "REACAO"],
            es: ["PLANO DE REACCION", "REACCION"],
            fr: ["PLAN DE REACTION", "REACTION"]
        },
        MASTER: {
            en: ["MASTER SHOT"],
            pt: ["PLANO MASTER", "MASTER"],
            es: ["PLANO MASTER", "MASTER"],
            fr: ["PLAN MASTER", "MASTER"]
        },
        ESTABLISHING: {
            en: ["ESTABLISHING SHOT", "ESTABLISHING"],
            pt: ["PLANO DE ESTABELECIMENTO", "ESTABLISHING"],
            es: ["PLANO DE ESTABLECIMIENTO", "ESTABLISHING"],
            fr: ["PLAN D ENSEMBLE", "PLAN D ETABLISSEMENT", "ESTABLISHING"]
        },
        CUTAWAY: {
            en: ["CUTAWAY"],
            pt: ["CORTE PARA", "CUTAWAY"],
            es: ["CORTE A", "CUTAWAY"],
            fr: ["CUTAWAY", "COUPE VERS"]
        }
    },
    move: {
        STATIC: {
            en: ["STATIC", "LOCKED OFF"],
            pt: ["ESTATICO", "FIXO"],
            es: ["ESTATICO", "FIJO"],
            fr: ["STATIQUE", "FIXE"]
        },
        PAN: {
            en: ["PAN", "PAN LEFT", "PAN RIGHT"],
            pt: ["PAN", "PANORAMICA"],
            es: ["PAN", "PANORAMICA"],
            fr: ["PAN", "PANORAMIQUE"]
        },
        TILT: {
            en: ["TILT", "TILT UP", "TILT DOWN"],
            pt: ["TILT", "INCLINA"],
            es: ["TILT", "INCLINA"],
            fr: ["TILT", "INCLINE"]
        },
        DOLLY_IN: {
            en: ["DOLLY IN", "PUSH IN", "TRACK IN"],
            pt: ["DOLLY IN", "APROXIMA", "ENTRA"],
            es: ["DOLLY IN", "ACERCA", "ENTRA"],
            fr: ["DOLLY IN", "AVANCE", "ENTRE"]
        },
        DOLLY_OUT: {
            en: ["DOLLY OUT", "PULL OUT", "TRACK OUT"],
            pt: ["DOLLY OUT", "AFASTA", "SAI"],
            es: ["DOLLY OUT", "ALEJA", "SALE"],
            fr: ["DOLLY OUT", "RECULE", "SORT"]
        },
        TRUCK: {
            en: ["TRUCK", "TRACK LEFT", "TRACK RIGHT", "SIDESTEP", "TRACKING SHOT"],
            pt: ["TRUCK", "LATERAL", "TRAVELLING LATERAL", "TRACKING SHOT"],
            es: ["TRUCK", "LATERAL", "TRAVELLING LATERAL"],
            fr: ["TRUCK", "LATERAL", "TRAVELLING LATERAL"]
        },
        PEDESTAL_UP: {
            en: ["PEDESTAL UP"],
            pt: ["PEDESTAL UP", "SOBE"],
            es: ["PEDESTAL UP", "SUBE"],
            fr: ["PEDESTAL UP", "MONTE"]
        },
        PEDESTAL_DOWN: {
            en: ["PEDESTAL DOWN"],
            pt: ["PEDESTAL DOWN", "DESCE"],
            es: ["PEDESTAL DOWN", "BAJA"],
            fr: ["PEDESTAL DOWN", "DESCEND"]
        },
        CRANE: {
            en: ["CRANE", "JIB"],
            pt: ["GRUA", "CRANE", "JIB"],
            es: ["GRUA", "CRANE", "JIB"],
            fr: ["GRUE", "CRANE", "JIB"]
        },
        ZOOM_IN: {
            en: ["ZOOM IN"],
            pt: ["ZOOM IN", "ZOOM APROXIMA"],
            es: ["ZOOM IN", "ZOOM ACERCA"],
            fr: ["ZOOM IN", "ZOOM AVANCE"]
        },
        ZOOM_OUT: {
            en: ["ZOOM OUT"],
            pt: ["ZOOM OUT", "ZOOM AFASTA"],
            es: ["ZOOM OUT", "ZOOM ALEJA"],
            fr: ["ZOOM OUT", "ZOOM RECULE"]
        },
        ARC: {
            en: ["ARC SHOT", "ARC"],
            pt: ["ARCO", "ARC"],
            es: ["ARCO", "ARC"],
            fr: ["ARC", "MOUVEMENT EN ARC"]
        },
        ORBIT: {
            en: ["ORBIT"],
            pt: ["ORBIT", "CIRCULA"],
            es: ["ORBIT", "CIRCULA"],
            fr: ["ORBIT", "TOURNE AUTOUR"]
        },
        WHIP_PAN: {
            en: ["WHIP PAN"],
            pt: ["WHIP PAN", "PAN RAPIDO"],
            es: ["WHIP PAN", "PAN RAPIDO"],
            fr: ["WHIP PAN", "PAN RAPIDE"]
        },
        HANDHELD: {
            en: ["HANDHELD", "HAND HELD"],
            pt: ["CAMERA NA MAO", "NA MAO"],
            es: ["CAMARA EN MANO", "EN MANO"],
            fr: ["CAMERA A L EPAULE", "A LA MAIN", "EN MAIN"]
        },
        STEADICAM: {
            en: ["STEADICAM"],
            pt: ["STEADICAM"],
            es: ["STEADICAM"],
            fr: ["STEADICAM"]
        },
        GIMBAL: {
            en: ["GIMBAL"],
            pt: ["GIMBAL"],
            es: ["GIMBAL"],
            fr: ["GIMBAL"]
        },
        DRONE: {
            en: ["DRONE", "AERIAL"],
            pt: ["DRONE", "AEREO"],
            es: ["DRONE", "AEREO"],
            fr: ["DRONE", "AERIEN"]
        }
    },
    focus: {
        RACK_FOCUS: {
            en: ["RACK FOCUS", "PULL FOCUS"],
            pt: ["RACK FOCUS", "MUDA O FOCO", "PUXAR FOCO"],
            es: ["RACK FOCUS", "CAMBIA EL FOCO"],
            fr: ["RACK FOCUS", "CHANGEMENT DE FOYER"]
        },
        DEEP_FOCUS: {
            en: ["DEEP FOCUS"],
            pt: ["DEEP FOCUS", "TUDO EM FOCO"],
            es: ["DEEP FOCUS", "TODO EN FOCO"],
            fr: ["DEEP FOCUS", "TOUT NET"]
        },
        SHALLOW_FOCUS: {
            en: ["SHALLOW FOCUS"],
            pt: ["SHALLOW FOCUS", "FUNDO DESFOCADO"],
            es: ["SHALLOW FOCUS", "FONDO DESENFOCADO"],
            fr: ["SHALLOW FOCUS", "FOND FLOU"]
        },
        SPLIT_DIOPTER: {
            en: ["SPLIT DIOPTER"],
            pt: ["SPLIT DIOPTER", "DIOPTRICO"],
            es: ["SPLIT DIOPTER", "DIOPTRICO"],
            fr: ["SPLIT DIOPTER", "DIOPTRIE"]
        }
    }
};
